<template>
  <!------------------------------------------
    This component displays the state profiles for GRPA to give to senators.
  -------------------------------------------->
  <div id="congressional-state-profiles">
    <PageTitle v-if="embedded === false" title="State Snapshots"/>

    <SideMarginsWrapper maxWidth="1300">
      <AboutResource v-if="embedded === false"
        class="mt-4"
        :updated="updated"
        :techContact1="{ name: 'Jordan', email: 'laytonj@lsc.gov', department: 'ODGA' }"
      >
        <p>
          This page provides a high-level overview of civil legal needs and services in the selected state. It is also available on (and thus styled to match) LSC.gov.
        </p>
        <p>
          The data presented will vary from state to state depending on what is available and compelling.
        </p>
      </AboutResource>

      <div class="lsc-dot-gov-styling no-print">
        <div class="select-menus select-menus-left">
          <div class="header-and-menu">
            <div class="select-header"><label for="st-st-select">Select a state</label></div>
            <StateSelect 
              @stateSelected="handleStateChange"
              :includeGU="false" 
              :lscDotGovVersion="true" 
              class="no-print" 
              selectMenuId="st-st-select" />
          </div>
        </div>
      </div>
      
      <div v-if="(selectedState === '' || selectedState === null || selectedState === undefined) && embedded === false">
        <AlertSelectGrantee
          text="Please select a state above"
          maxWidth="300"
        />
      </div>

      <div v-if="(selectedState === '' || selectedState === null || selectedState === undefined) && embedded === true" class="no-selection-text">
        Please select a state above to see a snapshot of civil legal needs and services, such as:
        <ul>
          <li>The gap between the civil legal needs of low-income residents and the resources available to meet those needs,</li>
          <li>Housing issues in the state,</li>
          <li>A map ofs LSC grantee(s) serving the state,</li>
          <li>The number of people served by LSC grantees in the state, and</li>
          <li>The top legal problems of civil legal aid cases.</li>
        </ul>
      </div>

      <div v-else-if="(selectedState !== '' && selectedState !== null && selectedState !== undefined) && loading">
        <LoadingSpinner />
      </div>
      <div v-if="profileData !== null && selectedState !== null && selectedState !== undefined && loading === false">
        <div id="print-version-switch" v-if="embedded === false" class="no-print">
          <v-switch
            v-model="printSwitch"
            label="Show as one-pager"
            class="no-print"
          ></v-switch>
        </div>

        <!-- WEB LAYOUT -->
        <StateProfilesWeb
          v-if="printSwitch === false"
          :key="selectedState" 
          :profileData="profileData" 
          :selectedState="selectedState"
          :granteesByType="granteesByType"
          :officesPerGrantee="officesPerGrantee"
          :isTerritory="isTerritory"
          :isTerritoryBesidesPuertoRico="isTerritoryBesidesPuertoRico"
        />

        <!-- PRINT LAYOUT -->
        <StateProfilesPrint
          v-if="printSwitch === true" 
          :key="selectedState" 
          :profileData="profileData" 
          :selectedState="selectedState"
          :granteesByType="granteesByType"
          :officesPerGrantee="officesPerGrantee"
          :isTerritory="isTerritory"
          :isTerritoryBesidesPuertoRico="isTerritoryBesidesPuertoRico"
        />

      </div>
    </SideMarginsWrapper>
  </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import StateSelect from '@/components/select/StateSelect'
import AboutResource from '@/components/common/AboutResource'
import AlertSelectGrantee from '@/components/common/AlertSelectGrantee'
import SideMarginsWrapper from '@/components/layout/SideMarginsWrapper'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import StateProfilesPrint from '@/views/advocacy/congress/state_profiles/StateProfilesPrint'
import StateProfilesWeb from '@/views/advocacy/congress/state_profiles/StateProfilesWeb'
import { servAreas2024 } from '@/js/serv_areas.js'
import { initPostHeight } from '@/js/iframe_postmessage.js'

export default {
  name: 'StateProfiles',

  components: { SideMarginsWrapper, AboutResource, AlertSelectGrantee, LoadingSpinner, PageTitle, StateSelect, StateProfilesPrint, StateProfilesWeb },

  data () {
    return {
      embedded: true,
      apiRoute: `${process.env.VUE_APP_BACKEND_NEW_DATA_API}`,
      loading: true,
      profileData: null,
      printSwitch: false,
      updated: '',
      officesPerGrantee: {},
      selectedState: null
    }
  },

  computed: {
    stateIsSelected () { return this.selectedState !== '' && this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '*' },
    granteesInState () {
      return servAreas2024.filter(x => x.states.indexOf(this.selectedState) > -1)
    },
    granteesByType () {
      let bfGeneral = this.granteesInState.filter(x => x.serv_area_type === 'Basic Field' && x.recipient_id !== '339026').sort((a, b) => a.org_name.localeCompare(b.org_name))
      let agWorker = this.granteesInState.filter(x => x.serv_area_type === 'Agricultural Worker').sort((a, b) => a.org_name.localeCompare(b.org_name))
      let nativeAm = this.granteesInState.filter(x => x.serv_area_type === 'Native American').sort((a, b) => a.org_name.localeCompare(b.org_name))
      let agWorkerNotBfGeneral = agWorker.filter(x => bfGeneral.map(x => x.org_name).indexOf(x.org_name) === -1)
      let nativeAmNotBfGeneral = nativeAm.filter(x => bfGeneral.map(x => x.org_name).indexOf(x.org_name) === -1)

      return {
        bfGeneral: bfGeneral,
        agWorker: agWorker,
        nativeAm: nativeAm,
        agWorkerNotBfGeneral: agWorkerNotBfGeneral,
        nativeAmNotBfGeneral: nativeAmNotBfGeneral
      }
    },
    isTerritory () {
      const territoriesFips = ['60', '66', '69', '72', '78']
      return territoriesFips.indexOf(this.selectedState) > -1
    },
    isTerritoryBesidesPuertoRico () {
      const territoriesFips = ['60', '66', '69', '78']
      return territoriesFips.indexOf(this.selectedState) > -1
    }
  },

  created () {
    if (this.stateIsSelected) {
      this.initGetData()
    }
  },

  mounted () {
    if (this.$route.query.print && this.$route.query.print === 'true') {
      this.printSwitch = true
    }
    initPostHeight('congressional-state-profiles')
  },
  
  methods: {
    async initGetData () {
      // Get data object for this state profile
      this.getProfileData()

      // Get offices for each BF General grantee IF 3 or fewer grantees
      let officesPerGrantee = {}

      if (this.granteesByType.bfGeneral.length < 4) {
        for (const rno of this.granteesByType.bfGeneral.map(x => x.recipient_id)) {
          officesPerGrantee[rno] = await this.getOffices(rno)
        }
      }

      // console.log('Offices per grantee:', officesPerGrantee)
      this.officesPerGrantee = officesPerGrantee
      return
    },

    async getProfileData () {
      if (!this.stateIsSelected) return

      this.loading = true
      let route = new URL(`${this.apiRoute}/public/project/congressional-state-profiles/${this.selectedState}/data.json`)
      
      let d = await fetch(route, {method: 'GET', redirect: 'follow'})
        .then((response) => response.json())
        .catch((err) => {
          console.log(err)
          this.profileData = null
        })
      
      if (d && d !== undefined && d!== null) { 
        this.profileData = d
        
        if ('last_updated' in d) {
          this.updated = `${d['last_updated'].substring(5,7)}/${d['last_updated'].substring(8,10)}/${d['last_updated'].substring(0,4)}`
        }
      }
      this.loading = false
      return
    },

    async getOffices (rno) {
      let data = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/grantee/${rno}/offices.json`)
      .then(function(response) { 
        return response.json() 
      })
      .catch( err => { console.log(err) })
      return data
    },

    handleStateChange (st) {
      this.selectedState = st
      window.top.postMessage({ state: st }, "*")
    }
  },

  watch: {
    selectedState (to) {
      if (this.$route.query.state !== to) {
        this.$router.push({ path: this.$route.path, query: { ...this.$route.query,state: to }})
      }
      this.initGetData()
    },
    $route: {
      immediate: true,
      handler (to) {
        if (to.name !== null) { // ignore $route before full path is ready
          if (to.query.embed && to.query.embed === "true") {
            this.embedded = true
          } else {
            this.embedded = false
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/lsc-dot-gov.scss';
#print-version-switch {
  display: flex;
  justify-content: flex-end;
}
.no-selection-text {
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 0.625rem;
  background-color: #daeefd;
  font-family: Lato, sans-serif;
  font-size: 1.25rem;

  ul {
    margin-top: 0.25rem;
    font-size: 1rem;
  }
  
  li {
    padding: 0.325rem 0;
  }
}
</style>